const DEFAULT_BREAKPOINT = 750;
const NEAR_VIEWPORT_MARGIN = '200px';
const READY_CLASS = 'is-ready';
const activeVideos = new Map();

function startListening(mediaQuery, handleChange) {
  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', handleChange);
    return;
  }

  if (typeof mediaQuery.addListener === 'function') mediaQuery.addListener(handleChange);
}

function stopListening(mediaQuery, handleChange) {
  if (typeof mediaQuery.removeEventListener === 'function') {
    mediaQuery.removeEventListener('change', handleChange);
    return;
  }

  if (typeof mediaQuery.removeListener === 'function') mediaQuery.removeListener(handleChange);
}

function getSourcesForDevice(sourceElements, deviceName) {
  return sourceElements.filter((sourceElement) => {
    const device = sourceElement.dataset.klVideoDevice;

    return device === deviceName || device === 'both';
  });
}

function getSourceSignature(sourceElements) {
  return sourceElements.map((sourceElement) => sourceElement.dataset.klVideoSrc).join('|');
}

function initVideo(videoElement) {
  if (activeVideos.has(videoElement)) return;

  const sourceElements = Array.from(videoElement.querySelectorAll('[data-kl-video-src]'));

  if (!sourceElements.length) return;

  const breakpoint = Number.parseInt(videoElement.dataset.klVideoBreakpoint, 10) || DEFAULT_BREAKPOINT;
  const desktopQuery = window.matchMedia('(min-width: ' + breakpoint + 'px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  let isNearViewport = false;
  let loadedSignature = null;

  function requestPlayback() {
    videoElement.muted = true;

    const playRequest = videoElement.play();

    if (!playRequest || typeof playRequest.catch !== 'function') return;

    playRequest.catch((error) => {
      if (error.name === 'AbortError') return;

      console.debug('kl-video: the browser refused to play this video', {
        source: videoElement.currentSrc,
        error,
      });
    });
  }

  function applySourcesForCurrentWidth() {
    const deviceName = desktopQuery.matches ? 'desktop' : 'mobile';
    const wantedSources = getSourcesForDevice(sourceElements, deviceName);

    if (!wantedSources.length) {
      loadedSignature = null;
      videoElement.pause();
      videoElement.classList.remove(READY_CLASS);
      return false;
    }

    const signature = getSourceSignature(wantedSources);

    if (signature === loadedSignature) return true;

    loadedSignature = signature;

    sourceElements.forEach((sourceElement) => {
      if (wantedSources.indexOf(sourceElement) === -1) {
        sourceElement.removeAttribute('src');
        return;
      }

      sourceElement.setAttribute('src', sourceElement.dataset.klVideoSrc);
    });

    videoElement.load();

    return true;
  }

  function teardown() {
    if (observer) observer.disconnect();

    stopListening(desktopQuery, handleQueryChange);
    stopListening(reducedMotionQuery, handleQueryChange);
    activeVideos.delete(videoElement);
  }

  function update() {
    if (!videoElement.isConnected) {
      teardown();
      return;
    }

    if (!isNearViewport) return;

    if (reducedMotionQuery.matches) {
      videoElement.pause();
      videoElement.classList.remove(READY_CLASS);
      return;
    }

    if (!applySourcesForCurrentWidth()) return;

    if (!videoElement.paused) return;

    requestPlayback();
  }

  function handleQueryChange() {
    update();
  }

  function handleIntersection(entries) {
    entries.forEach((entry) => {
      isNearViewport = entry.isIntersecting;

      if (entry.isIntersecting) {
        update();
        return;
      }

      videoElement.pause();
    });
  }

  const observer =
    typeof IntersectionObserver === 'function'
      ? new IntersectionObserver(handleIntersection, { rootMargin: NEAR_VIEWPORT_MARGIN })
      : null;

  function handleReady() {
    videoElement.classList.add(READY_CLASS);
  }

  function handleFailure(event) {
    videoElement.classList.remove(READY_CLASS);

    console.debug('kl-video: this video could not be played, the image below it stands in', {
      source: (event.target && event.target.src) || videoElement.currentSrc,
    });
  }

  videoElement.addEventListener('playing', handleReady);

  videoElement.addEventListener('error', handleFailure, true);

  activeVideos.set(videoElement, teardown);

  startListening(desktopQuery, handleQueryChange);
  startListening(reducedMotionQuery, handleQueryChange);

  if (!observer) {
    isNearViewport = true;
    update();
    return;
  }

  observer.observe(videoElement);
}

function releaseDetachedVideos() {
  activeVideos.forEach((teardown, videoElement) => {
    if (videoElement.isConnected) return;

    teardown();
  });
}

function initVideos(rootElement) {
  releaseDetachedVideos();
  rootElement.querySelectorAll('[data-kl-video]').forEach(initVideo);
}

initVideos(document);

document.addEventListener('shopify:section:load', (event) => {
  initVideos(event.target);
});

document.addEventListener('shopify:section:unload', releaseDetachedVideos);
